import React, { useMemo } from 'react';
import {
  AsyncConnector,
  BadRequestError,
  Button,
  CondensedHeader,
  CondensedLarge,
  Dropdown,
  Form,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  ToastUtil,
} from '@axiom/ui';
import type { Account, Contact } from '@axiom/validation';
import { AhqFindAccountSchema } from '@axiom/validation';

import { AccountsApi } from '../../api/accounts-api';
import { ContactsApi } from '../../api/contacts-api';
import { ContactsLegacyApi } from '../../api/contacts-legacy-api';

export type FindAccountForContactModalProps = {
  onClose: () => void;
  onSuccess?: () => void;
  contactId: Contact['id'];
};

type ReadAccountsResponseType = {
  data: Account[];
  meta: { currentPage: number; pageCount: number; resultCount: number };
};

export const FindAccountForContactModal = ({
  onClose,
  onSuccess,
  contactId,
}: FindAccountForContactModalProps) => {
  const accountsConnector = useMemo(
    () =>
      new AsyncConnector<ReadAccountsResponseType>({
        getData: args => {
          return AccountsApi.readAccounts({
            search: args.search ?? '',
            page: args.page,
          });
        },
        parseData: results => {
          const accounts = results.data.map(account => ({
            id: account.id,
            name: account.calculatedName,
          }));

          return {
            currentPage: results.meta.currentPage,
            totalPages: results.meta.pageCount,
            data: accounts,
          };
        },
      }),
    []
  );

  return (
    <Form
      name="FIND_ACCOUNT_FOR_CONTACT_FORM"
      schema={AhqFindAccountSchema}
      initialValues={{
        accountId: undefined,
      }}
      onSubmit={async formData => {
        const { accountId } = formData;
        if (accountId) {
          try {
            await ContactsLegacyApi.createContactAccounts({
              contactId,
              accountIds: [accountId],
            });
            await ContactsApi.invalidateContact(contactId);
          } catch (error) {
            if (!(error instanceof BadRequestError)) throw error;
            const message =
              (error.getErrors() as { message?: string } | undefined)
                ?.message ?? 'An unknown issue occurred';
            ToastUtil.add({
              dismissible: true,
              name: 'FIND_ACCOUNT_FOR_CONTACT_ERROR',
              type: 'error',
              children: (
                <>
                  <CondensedHeader>Error</CondensedHeader>
                  <CondensedLarge>{message}</CondensedLarge>
                </>
              ),
            });
            return;
          }
          ToastUtil.add({
            dismissible: true,
            name: 'FIND_ACCOUNT_FOR_CONTACT_SUCCESS',
            type: 'info',
            children: <CondensedHeader>Account added.</CondensedHeader>,
          });
          onSuccess?.();
          onClose();
        }
      }}
    >
      {({ fireSubmit }) => (
        <Modal name="FIND_ACCOUNT_FOR_CONTACT_MODAL">
          <ModalHeader
            name="FIND_ACCOUNT_FOR_CONTACT_MODAL_HEADER"
            onClose={() => {
              onClose();
            }}
          >
            Search Accounts
          </ModalHeader>
          <ModalSection
            name="FIND_ACCOUNT_FOR_CONTACT_MODAL_SECTION"
            disableScrolling
          >
            <Dropdown
              name="accountId"
              options={accountsConnector}
              displayKey="name"
              valueKey="id"
              placeholder="Type to start searching..."
            />
          </ModalSection>
          <ModalFooter name="FIND_ACCOUNT_FOR_CONTACT_MODAL_FOOTER">
            <Button
              onClick={() => {
                onClose();
              }}
              name="CANCEL"
              variation="outline"
            >
              Cancel
            </Button>
            <Button type="submit" onClick={fireSubmit} name="ADD">
              Add
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
